export type OverviewTrend = 'up' | 'down' | 'flat';

export class OverviewWallet {
  balanceCents!: number;
  balance!: string;
  currency!: string;
  status!: 'active' | 'overdrawn';

  constructor(data: any = {}) {
    this.balanceCents = data.balance_cents;
    this.balance = data.balance;
    this.currency = data.currency;
    this.status = data.balance_cents < 0 ? 'overdrawn' : 'active';
  }
}

/** One KPI tile value is pre-formatted for display (backend owns rounding/units,
 *  same convention as Wallet.balance being a formatted string alongside balanceCents). */
export class OverviewStat {
  key!: string;
  label!: string;
  value!: string;
  caption!: string;
  deltaPct!: number;
  trend!: OverviewTrend;
  /** Which delta direction reads as an improvement for this metric rising
   *  requests is good, rising error rate is not, so color-coding can't just
   *  assume "up is green" across every tile. */
  goodDirection!: 'up' | 'down';
  series: number[] = [];

  constructor(data: any = {}) {
    this.key = data.key;
    this.label = data.label;
    this.value = data.value;
    this.caption = data.caption;
    this.deltaPct = data.delta_pct ?? 0;
    this.trend = this.deltaPct > 0 ? 'up' : this.deltaPct < 0 ? 'down' : 'flat';
    this.goodDirection = data.good_direction ?? 'up';
    this.series = data.series ?? [];
  }

  get isImprovement(): boolean {
    return this.trend !== 'flat' && this.trend === this.goodDirection;
  }
}

export class OverviewSeriesPoint {
  date!: string;
  value!: number;

  constructor(data: any = {}) {
    this.date = data.date;
    this.value = data.value;
  }
}

export class OverviewEndpointStat {
  endpoint!: string;
  requests!: number;

  constructor(data: any = {}) {
    this.endpoint = data.endpoint;
    this.requests = data.requests;
  }
}

export class OverviewApiKeysSummary {
  activeCount!: number;
  mostRecentlyUsed: { namePrefix: string; usedAt: string } | null = null;

  constructor(data: any = {}) {
    this.activeCount = data.active_count;
    this.mostRecentlyUsed = data.most_recently_used
      ? { namePrefix: data.most_recently_used.name_prefix, usedAt: data.most_recently_used.used_at }
      : null;
  }
}

/** Matches WalletTransactionKind exactly (see transaction.model.ts) this
 *  feed is now the wallet's real 5 most-recent transactions (via
 *  OverviewService → GET /wallet), so, per-call API spend was never part of
 *  that ledger and 'usage' is deliberately not a value here. */
export type OverviewActivityKind = 'topup' | 'grant' | 'adjustment' | 'refund';

export class OverviewActivityItem {
  id!: string;
  occurredAt!: string;
  kind!: OverviewActivityKind;
  description!: string;
  amount!: string;
  currency!: string;
  isCredit!: boolean;
  status!: string;

  constructor(data: any = {}) {
    this.id = data.id;
    this.occurredAt = data.occurred_at;
    this.kind = data.kind;
    this.description = data.description;
    this.amount = data.amount;
    this.currency = data.currency;
    this.isCredit = !!data.is_credit;
    this.status = data.status;
  }
}

/** The full response shape a `GET /dashboard` backend endpoint would return in one
 *  call see OverviewService for why this doesn't exist yet and what it stands in for. */
export class OverviewResult {
  range!: { from: string; to: string };
  wallet!: OverviewWallet;
  stats: OverviewStat[] = [];
  series: OverviewSeriesPoint[] = [];
  topEndpoints: OverviewEndpointStat[] = [];
  apiKeys!: OverviewApiKeysSummary;
  recentActivity: OverviewActivityItem[] = [];

  constructor(data: any = {}) {
    this.range = data.range;
    this.wallet = new OverviewWallet(data.wallet);
    this.stats = (data.stats ?? []).map((s: object) => new OverviewStat(s));
    this.series = (data.series ?? []).map((s: object) => new OverviewSeriesPoint(s));
    this.topEndpoints = (data.top_endpoints ?? []).map((e: object) => new OverviewEndpointStat(e));
    this.apiKeys = new OverviewApiKeysSummary(data.api_keys);
    this.recentActivity = (data.recent_activity ?? []).map((a: object) => new OverviewActivityItem(a));
  }

  /** First-run checklist on Home, derived only from data already on this
   *  response: a key exists, and some key has been used. */
  get setupSteps(): { hasKey: boolean; firstCall: boolean } {
    return {
      hasKey: this.apiKeys.activeCount > 0,
      firstCall: !!this.apiKeys.mostRecentlyUsed,
    };
  }

  get setupComplete(): boolean {
    const s = this.setupSteps;
    return s.hasKey && s.firstCall;
  }
}
